import React, { useMemo, useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import RobitoLogo from "../components/AppLogo";
import { apiPostPublic, apiPostAuth } from "../utils/api";

function TopBar({ onToggleMenu, menuOpen }) {
  return (
    <header className="header">
      <div className="left" />
      <div className="logo">
        <RobitoLogo />
      </div>
      <div className="right account-menu-anchor">
        <button
          className="icon-btn"
          onClick={onToggleMenu}
          title="Settings"
          aria-label="Settings"
          aria-expanded={menuOpen ? "true" : "false"}
          aria-haspopup="menu"
        >
          <i className="ti-settings" style={{ fontSize: 18, color: "white" }}></i>
        </button>
      </div>
    </header>
  );
}

function formatExpiryDate(expiryRaw) {
  if (!expiryRaw) return "-";

  // Expecting YYYYMMDD format
  if (/^\d{8}$/.test(expiryRaw)) {
    const y = expiryRaw.slice(0, 4);
    const m = expiryRaw.slice(4, 6);
    const d = expiryRaw.slice(6, 8);

    // Validate date parts
    const year = Number(y);
    const month = Number(m);
    const day = Number(d);
    const dt = new Date(year, month - 1, day);

    if (
      dt.getFullYear() === year &&
      dt.getMonth() === month - 1 &&
      dt.getDate() === day
    ) {
      // Return DD-MM-YYYY
      return `${String(day).padStart(2, "0")}-${String(month).padStart(
        2,
        "0"
      )}-${year}`;
    }
  }

  return expiryRaw; // fallback
}


function SiteList({ items = [], onSelect }) {
  return (
    <div className="list" role="list">
      {items.map((item) => (
        <article
          className="list-item"
          key={item.id}
          role="listitem"
          onClick={() => onSelect?.(item)}
          style={{ cursor: "pointer" }}
        >
          <div>
            <div className="item-title">{item.title}</div>
            <div className="item-sub">{item.description}</div>
            <div className="item-expiry">
              Aktif Sampai: {item.expiryLabel || "-"}
            </div>
          </div>
          <div className="chevron">›</div>
        </article>
      ))}
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <button className="btn block add-site-btn" onClick={onAdd}>
          Buat Website
        </button>
      </div>

      <div className="empty-wrap">
        <div className="empty-card">
          <h3 style={{ margin: "4px 0" }}>Belum ada website</h3>
          <p className="small">
            Yuk buat website pertama kamu sekarang. Tinggal pilih paket, tentukan alamat website, dan isi
            informasi website kamu.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Account dropdown */
function AccountMenu({ onClose, onLogout }) {
  const ref = useRef(null);

  // Close on outside click / Esc
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [onClose]);

  return (
    <div ref={ref} className="account-menu" role="menu" aria-label="Account menu">
      <div className="section">
        <Link className="menu-item" to="/dashboard/account-settings" onClick={onClose}>
          <span className="ti-user" /> <span>Pengaturan Akun</span>
        </Link>
        {/*
        <Link className="menu-item" to="/dashboard/referral" onClick={onClose}>
          <span className="ti-gift" /> <span>Referral</span>
        </Link>
        */}
        <a
          className="menu-item"
          href="/help-faq"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
        >
          <span className="ti-help-alt" /> <span>Bantuan & FAQ</span>
        </a>
        <a
          className="menu-item"
          href="/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
        >
          <span className="ti-shield" /> <span>Kebijakan Privasi</span>
        </a>
        <a
          className="menu-item"
          href="/terms-of-service"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
        >
          <span className="ti-agenda" /> <span>Ketentuan Penggunaan</span>
        </a>
      </div>

      <div className="divider" />

      {/* Logout pinned at bottom */}
      <button className="menu-item logout" onClick={onLogout}>
        <span className="ti-power-off" /> <span>Keluar</span>
      </button>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();

  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter(
      (s) =>
        (s.title && s.title.toLowerCase().includes(q)) ||
        (s.description && s.description.toLowerCase().includes(q))
    );
  }, [sites, query]);

  // Dropdown
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen((v) => !v);
  const closeMenu = () => setMenuOpen(false);

  // Load site list
  useEffect(() => {
    let cancelled = false;

    const loadSites = async () => {
      setLoading(true);
      setLoadError("");

      try {
        const res = await apiPostAuth("/site/get_site_list.php", {});

        // Flexible handling depending on backend payload
        const rawList =
          (res && Array.isArray(res.site_list) && res.site_list) || [];

        const mapped = rawList
          .filter((s) => s && s.id)
          .map((s) => ({
            id: s.id,
            title: s.site_title || s.site_address || `Site ${s.id}`,
            description: s.site_address
              ? `Alamat: ${s.site_address}`
              : "",
            expiryLabel: formatExpiryDate(s.expiry_date),
          }));

        if (!cancelled) {
          setSites(mapped);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError("Gagal memuat daftar website.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSites();

    return () => {
      cancelled = true;
    };
  }, []);

  // Actions
  const handleAdd = () => navigate("/dashboard/create");
  const handleSelect = (item) => navigate(`/dashboard/site/${item.id}`);
  const handleAccount = () => toggleMenu();

  const handleLogout = async () => {
    try {
      const response = await apiPostPublic("logout.php");
      if (response?.status === "success" || response?.result === 1) {
        navigate("/login");
      } else {
        alert("Logout gagal. Silakan coba lagi.");
      }
    } catch (error) {
      alert("Oops, something went wrong");
    }
  };

  return (
    <div className="app-shell">
      <TopBar onToggleMenu={handleAccount} menuOpen={menuOpen} />

      {/* Dropdown anchored to top-right; positioned via CSS */}
      {menuOpen && (
        <AccountMenu onClose={closeMenu} onLogout={handleLogout} />
      )}

      <main className="main">
        {/* Optional search (query state already exists) */}
        {sites.length > 0 && !loading && !loadError && (
          <div className="section-head">
            <button className="btn block add-site-btn" onClick={handleAdd}>
              Tambah Website
            </button>
          </div>
        )}

        {loading ? (
          <div className="site-message">Memuat daftar website…</div>
        ) : loadError ? (
          <div className="site-message site-message-error">
            {loadError}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState onAdd={handleAdd} />
        ) : (
          <SiteList items={filtered} onSelect={handleSelect} />
        )}
      </main>
    </div>
  );
}

export default Dashboard;
